import { useState, useEffect, useRef } from "react";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import TableResultsComponent from "./TableResults";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { trackEvent, LOCATION_SEARCH } from "../utils/googleAnalytics";
import { settings } from "../config/settings";
import {
  isTexasGeocoderFeature,
  TEXAS_BBOX,
  TEXAS_PROXIMITY,
} from "../utils/texasGeocoder";

/** URL-safe Base64 (no regex; linear time). */
function geoJsonToUrlSafeBase64(geojson) {
  const raw = btoa(JSON.stringify(geojson));
  let out = "";
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    if (c === "/") {
      out += "_";
    } else if (c === "+") {
      out += "-";
    } else {
      out += c;
    }
  }
  let end = out.length;
  while (end > 0 && out[end - 1] === "=") end -= 1;
  return out.slice(0, end);
}

const Location = ({ token }) => {
  const geocoderContainerRef = useRef(null);
  const geocoderRef = useRef(null);
  const initializedRef = useRef(false);
  const [error, setError] = useState(null);
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState("");
  const [key, setKey] = useState(0);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [isResultsExpanded, setIsResultsExpanded] = useState(true);

  const handlePlaceChanged = (event) => {
    try {
      trackEvent(LOCATION_SEARCH, "Location Search Page");
      const place = event.result;
      if (place.bbox) {
        const [swLng, swLat, neLng, neLat] = place.bbox;
        const geojson = {
          type: "Polygon",
          coordinates: [
            [
              [swLng, swLat],
              [neLng, swLat],
              [neLng, neLat],
              [swLng, neLat],
              [swLng, swLat],
            ],
          ],
        };
        // Convert the GeoJSON object to a URL-safe Base64 string (no ReDoS-prone regex).
        setSubmittedSearchTerm(geoJsonToUrlSafeBase64(geojson));
        setKey((prevKey) => prevKey + 1);
        // Keep search expanded so the input remains visible
        setIsSearchExpanded(true);
        setIsResultsExpanded(true);
      }
    } catch (error) {
      setError(
        "We're having trouble retrieving results for your search. Please check your input and try again.",
      );
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (!initializedRef.current && geocoderContainerRef.current) {
      const geocoder = new MapboxGeocoder({
        accessToken: settings.mapboxKey,
        placeholder: "Type a Texas city or county name and press Enter...",
        countries: "us",
        // [minLon, minLat, maxLon, maxLat] — biases API results toward Texas
        bbox: TEXAS_BBOX,
        proximity: TEXAS_PROXIMITY,
        // Hard-drop non-Texas suggestions (bbox/proximity alone are not strict)
        filter: isTexasGeocoderFeature,
        mapboxgl: null,
        types: "place,locality,district",
        autocomplete: true,
        fuzzyMatch: true,
        limit: 10,
      });

      geocoderRef.current = geocoder;
      geocoderContainerRef.current.appendChild(geocoder.onAdd());

      geocoder.on("result", (event) => {
        handlePlaceChanged(event);
      });

      initializedRef.current = true;
    }
  }, []);

  // Re-render geocoder when accordion is expanded
  useEffect(() => {
    if (
      isSearchExpanded &&
      initializedRef.current &&
      geocoderRef.current &&
      geocoderContainerRef.current
    ) {
      // Force a re-render of the geocoder to ensure it's visible
      setTimeout(() => {
        if (geocoderRef.current && geocoderContainerRef.current) {
          // This helps ensure the geocoder is properly displayed
          const geocoderElement = geocoderContainerRef.current.querySelector(
            ".mapboxgl-ctrl-geocoder",
          );
          if (geocoderElement) {
            geocoderElement.style.display = "block";
            geocoderElement.style.visibility = "visible";
            const popupOpen = document.querySelector(".popup-overlay");
            if (popupOpen) {
              geocoderElement.style.zIndex = "1";
            }
          }
        }
      }, 100);
    }
  }, [isSearchExpanded]);

  // Toggle functions for expanding/collapsing sections
  const toggleSearchSection = (e) => {
    if (e) {
      e.preventDefault();
    }
    setIsSearchExpanded(!isSearchExpanded);
  };

  const toggleResultsSection = (e) => {
    if (e) {
      e.preventDefault();
    }
    setIsResultsExpanded(!isResultsExpanded);
  };

  return (
    <div className="search-container-1">
      {/* Search Card */}
      <div className="content-card location-search-card">
        <button
          type="button"
          className="card-header"
          onClick={toggleSearchSection}
          aria-expanded={isSearchExpanded}
          aria-controls="location-section"
          aria-label={isSearchExpanded ? "Collapse search" : "Expand search"}
        >
          <span className="card-title">Location Search</span>
          <span className="accordion-controls" aria-hidden="true">
            <span
              className={`toggle-btn ${
                isSearchExpanded ? "expanded" : "collapsed"
              }`}
            >
              {isSearchExpanded ? (
                <FaChevronUp className="up-rotate" />
              ) : (
                <FaChevronDown className="down-rotate" />
              )}
            </span>
          </span>
        </button>
        <div
          className={`card-body ${isSearchExpanded ? "expanded" : "collapsed"}`}
        >
          <div className="location-search-container">
            <div className="search-instructions">
              <p>
                Type a city or county name to search by location. Select from
                the suggestions to fetch models located within the specified
                area.
              </p>
            </div>
            <div
              className="geocoder-container"
              ref={geocoderContainerRef}
            ></div>
            {error && <div className="error-message">{error}</div>}
          </div>
        </div>
      </div>

      {/* Results Card */}
      {submittedSearchTerm && (
        <div className="content-card">
          <button
            type="button"
            className="card-header"
            onClick={toggleResultsSection}
            aria-expanded={isResultsExpanded}
            aria-controls="results-section"
            aria-label={
              isResultsExpanded ? "Collapse results" : "Expand results"
            }
          >
            <span className="card-title">Results</span>
            <span className="accordion-controls" aria-hidden="true">
              <span
                className={`toggle-btn ${
                  isResultsExpanded ? "expanded" : "collapsed"
                }`}
              >
                {isResultsExpanded ? (
                  <FaChevronUp className="up-rotate" />
                ) : (
                  <FaChevronDown className="down-rotate" />
                )}
              </span>
            </span>
          </button>
          <div
            className={`card-body ${
              isResultsExpanded ? "expanded" : "collapsed"
            }`}
          >
            <TableResultsComponent
              key={key}
              submittedSearchTerm={JSON.stringify(submittedSearchTerm)}
              path="/location/search"
              ssp={true}
              token={token}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Location;
